// src/encounter/lib/to-education-article.ts
// Map education search hits (Article) ⇄ the persist shape (AssessmentArticles), Flutter-faithful.
// Flutter `new_ap_bloc.addEducation`: build AssessmentArticles{ encounter, subject, performer,
// onSetDate: now, display, articleUrl, summary }; dedup by `display`. Persisted flat in
// PatientAssessment.assessmentPlanArticles (NO Dx link — parentId null).
import type { Article } from '@/types/education-api';
import type { AssessmentArticles } from '@/types/assessment-api';

/** App-state shape for a picked/loaded education article (what the canvas EDUCATION section renders). */
export interface EducationArticle {
  /** The persisted id (present on loaded articles; absent on a freshly-added one). */
  id?: string;
  /** Article title — also the dedup key (Flutter dedups by display). */
  display: string;
  /** Opened in the preview webview. */
  articleUrl?: string;
  /** Short blurb; the card description falls back to 'Article' when blank (Flutter parity). */
  summary?: string;
  /**
   * When this article was given, as persisted. Carried so a re-save PRESERVES it — Flutter stamps
   * onSetDate ONCE, at add time (new_ap_bloc.dart:205-213). We were re-stamping every saved article on
   * every autosave, so the "given at" time drifted to the last save (PROD-1801).
   */
  onSetDate?: string;
  /** The persisted `endDate`, sent back unchanged — Flutter's AssessmentArticles round-trips it (PROD-2854). */
  endDate?: string;
}

/** Context stamped onto every saved article (Flutter addEducation). `now` injectable for tests. */
export interface EducationContext {
  encounter?: string | null;
  subject?: string | null; // patientId
  performer?: string | null; // providerId
  now?: string; // ISO; defaults to new Date().toISOString()
}

/**
 * Stamp `onSetDate` once on an article that is NEW on this visit (PROD-2854; used by the bundle
 * import — an order-set article is stamped in orderSetItemToArticle). Without a stamp the save
 * mapper's "new article → now" fallback runs on every save, so the article would take the time of the
 * latest save until a reload. A date already present is kept.
 */
export function stampNewArticle(article: EducationArticle, now: string = new Date().toISOString()): EducationArticle {
  return article.onSetDate ? article : { ...article, onSetDate: now };
}

/**
 * Add an article picked from the A/P search rail (PROD-2867). Flutter `addEducation`
 * (new_ap_bloc.dart:205) creates it with `onSetDate: DateTime.now()` and keeps it. The backend
 * re-creates A/P rows on every save, so an unstamped article took the time of each save until a
 * reload. Stamps once, here.
 *
 * INVARIANT: the existing list goes FIRST into dedupeArticles (first wins). Re-picking an article
 * that is already on the list (loaded from the server, or added earlier) therefore keeps that row and
 * its original onSetDate; the fresh stamp on the duplicate is dropped. Do not swap the order.
 * `now` is a default parameter, so it is evaluated on every call, not once at module load.
 */
export function addArticleWithOnSet(
  articles: EducationArticle[],
  article: EducationArticle,
  now: string = new Date().toISOString(),
): EducationArticle[] {
  return dedupeArticles([...articles, stampNewArticle(article, now)]);
}

/**
 * The Elsevier result types GetArticles puts in `Summary` (PROD-3147). Scrips.Master
 * ArticleRepository.GetElSevierArticles sets `Summary = root.ResultType` for every hit and searches
 * `searchtype=di,dis,l,v`. The code names "medications" (the `dis` URL path) and "video" literally; "problems"
 * is the value the dev E2E test saw (8 Oct). The label for `l` (labs) is not in the code, so it is not listed.
 */
export const ARTICLE_RESULT_TYPES: ReadonlySet<string> = new Set(['problems', 'medications', 'video']);

/**
 * The blurb to show under an article's title, or undefined (the card then says "Article").
 *
 * PROD-3147: the saved `summary` is the hit's Elsevier result type, not a blurb, so after a reload it showed as a
 * stray "problems" under the title. A known result type (any case, trimmed) is dropped; any other text is shown.
 * The stored value is left as it is (it round-trips on save unchanged); only the display drops it.
 */
export function articleBlurb(summary: string | null | undefined): string | undefined {
  const text = (summary ?? '').trim();
  if (!text || ARTICLE_RESULT_TYPES.has(text.toLowerCase())) return undefined;
  return text;
}

/** A search hit → the app article shape. */
export function articleToEducation(article: Article): EducationArticle {
  return {
    display: article.display ?? '',
    ...(article.articleUrl ? { articleUrl: article.articleUrl } : {}),
    ...(article.summary ? { summary: article.summary } : {}),
  };
}

/**
 * Dedup a list of articles, first-wins. Flutter dedups by `display` (addEducation firstWhereOrNull),
 * but the real GetArticles response carries a stable `articleUrl`/`id`, so we key by
 * `articleUrl ?? id ?? display` — strictly safer than the human title alone (two distinct articles
 * with the same title but different URLs no longer silently collapse; #159 review). Falls back to
 * `display` only when neither is present.
 */
export function dedupeArticles(articles: EducationArticle[]): EducationArticle[] {
  const seen = new Set<string>();
  const out: EducationArticle[] = [];
  for (const a of articles) {
    const key = a.articleUrl ?? a.id ?? a.display;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(a);
  }
  return out;
}

/** App articles → the persist array (stamps encounter/subject/performer/onSetDate; dedups by display). */
export function educationToRequest(articles: EducationArticle[], ctx: EducationContext): AssessmentArticles[] {
  const onSetDate = ctx.now ?? new Date().toISOString();
  return dedupeArticles(articles).map((a) => ({
    ...(a.id ? { id: a.id } : {}),
    encounter: ctx.encounter ?? null,
    subject: ctx.subject ?? null,
    performer: ctx.performer ?? null,
    parentId: null, // education has NO Dx link
    display: a.display,
    articleUrl: a.articleUrl ?? null,
    summary: a.summary ?? null,
    // Preserve an existing stamp; only a NEWLY added article gets `now`.
    onSetDate: a.onSetDate ?? onSetDate,
    ...(a.endDate ? { endDate: a.endDate } : {}),
    isDeleted: false,
  }));
}

/** Persisted articles → app shape for the canvas (drops soft-deleted rows). */
export function assessmentArticlesToEducation(list: AssessmentArticles[] | undefined | null): EducationArticle[] {
  return (list ?? [])
    .filter((a) => a.isDeleted !== true)
    .map((a) => ({
      // Carry the persisted stamp so a re-save preserves it (see EducationArticle.onSetDate).
      ...(a.onSetDate ? { onSetDate: a.onSetDate } : {}),
      ...(a.endDate ? { endDate: a.endDate } : {}),
      ...(a.id ? { id: a.id } : {}),
      display: a.display ?? '',
      ...(a.articleUrl ? { articleUrl: a.articleUrl } : {}),
      ...(a.summary ? { summary: a.summary } : {}),
    }));
}
